import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';
import { ManagedUserListItemDto, UserManagementService } from '../services/user-management.service';

@Component({
  selector: 'app-users-management-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <section class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/" class="home-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                 viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Home
          </a>
          <div class="hero-top-row">
            <div class="hero-icon hero-icon--amber">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26"
                   viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <app-hero-user-pill></app-hero-user-pill>
          </div>
          <h1 class="page-title">Organization Users</h1>
          <p class="page-sub">Browse organization users, roles, and open user detail including uploaded media.</p>
        </div>
      </section>

      <section class="page-body">
        <div class="page-body-inner">
          @if (message()) {
            <div class="status-banner">{{ message() }}</div>
          }

          <div class="card">
            <div class="card-hd">
              <span class="card-title">Users</span>
              <div class="card-tools">
                <input class="tool-input" placeholder="Filter users…" [(ngModel)]="filterText" />
                <select class="tool-select" [(ngModel)]="sortMode">
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                  <option value="org">Organization</option>
                  <option value="role">Role</option>
                </select>
              </div>
            </div>

            <div class="table-wrap">
              <div class="table-head">
                <span>Name</span>
                <span>Email</span>
                <span>Organization</span>
                <span>Role</span>
                <span>SMS</span>
                <span>Media Upload</span>
              </div>
              @for (item of filteredUsers(); track trackBy(item)) {
                <button type="button" class="table-row table-row--btn" (click)="openDetail(item.userId)">
                  <span>{{ fullName(item) }}</span>
                  <span>{{ item.email || '—' }}</span>
                  <span>{{ item.organizationName || ('Org ' + item.organizationId) }}</span>
                  <span>{{ item.role }}</span>
                  <span>{{ item.smsNotificationsEnabled ? 'Opted In' : 'Not Opted In' }}</span>
                  <span>{{ item.mediaUploadConsentEnabled ? 'Opted In' : 'Not Opted In' }}</span>
                </button>
              } @empty {
                <div class="empty">No users found.</div>
              }
            </div>
          </div>
        </div>
      </section>
    </div>
  `,
  styles: [`
    .page { min-height: 100vh; }
    .page-hero {
      border-bottom: 1px solid var(--color-border);
      padding: 3rem 1.5rem 3.5rem;
      background: radial-gradient(ellipse at 12% 60%, color-mix(in srgb, #f59e0b 8%, transparent) 0%, transparent 55%), linear-gradient(155deg, var(--color-bg-start) 0%, var(--color-bg-end) 100%);
    }
    .page-hero-inner, .page-body-inner { max-width: 1100px; margin: 0 auto; }
    .home-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      color: var(--color-muted);
      text-decoration: none;
      margin-bottom: .95rem;
    }
    .hero-top-row {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 0.85rem;
    }
    .hero-icon {
      width: 3.25rem;
      height: 3.25rem;
      border-radius: .95rem;
      display: grid;
      place-items: center;
      border: 1px solid color-mix(in srgb, #f59e0b 45%, #0000);
      color: #fbbf24;
      background: color-mix(in srgb, #f59e0b 18%, transparent);
      box-shadow: inset 0 0 0 1px color-mix(in srgb, #f59e0b 24%, transparent);
    }
    .hero-icon--amber { color: #fbbf24; }
    .page-title { margin: .6rem 0 0; }
    .page-sub { margin: .4rem 0 0; color: var(--color-muted); }
    .page-body{padding:1.5rem}.status-banner{margin-bottom:.8rem;border:1px solid var(--color-border);border-radius:.6rem;padding:.55rem .75rem}
    .card{border:1px solid var(--color-border);border-radius:.9rem;overflow:hidden;background:color-mix(in srgb,var(--color-panel) 80%,transparent)}
    .card-hd{display:flex;justify-content:space-between;gap:.6rem;flex-wrap:wrap;border-bottom:1px solid var(--color-border);padding:.8rem 1rem}
    .card-title{font-weight:600}.card-tools{display:flex;gap:.45rem;flex-wrap:wrap}.tool-input,.tool-select{border:1px solid var(--color-border);border-radius:.45rem;background:transparent;color:var(--color-text);padding:.38rem .5rem}
    .table-head,.table-row{display:grid;grid-template-columns:1.3fr 1.3fr 1fr .8fr .9fr .9fr;gap:.75rem;padding:.7rem 1rem;align-items:center}
    .table-head{font-size:.7rem;text-transform:uppercase;letter-spacing:.06em;color:var(--color-muted);border-bottom:1px solid var(--color-border)}
    .table-row{border-bottom:1px solid color-mix(in srgb,var(--color-border) 50%,transparent)}.table-row:last-child{border-bottom:none}
    .table-row--btn{width:100%;text-align:left;background:transparent;border:0;color:inherit;cursor:pointer}.table-row--btn:hover{background:color-mix(in srgb,var(--color-accent) 8%,transparent)}
    .empty{padding:1.2rem;color:var(--color-muted);text-align:center}
  `]
})
export class UsersManagementPageComponent implements OnInit {
  private readonly service = inject(UserManagementService);
  private readonly router = inject(Router);

  readonly users = signal<ManagedUserListItemDto[]>([]);
  readonly message = signal<string>('');

  filterText = '';
  sortMode: 'name-asc' | 'name-desc' | 'org' | 'role' = 'name-asc';

  readonly filteredUsers = computed(() => {
    const q = this.filterText.trim().toLowerCase();
    const filtered = !q
      ? [...this.users()]
      : this.users().filter(item =>
          this.fullName(item).toLowerCase().includes(q)
          || item.email.toLowerCase().includes(q)
          || item.organizationName.toLowerCase().includes(q)
          || item.role.toLowerCase().includes(q));

    return filtered.sort((a, b) => {
      if (this.sortMode === 'name-asc') return this.fullName(a).localeCompare(this.fullName(b));
      if (this.sortMode === 'name-desc') return this.fullName(b).localeCompare(this.fullName(a));
      if (this.sortMode === 'org') return a.organizationName.localeCompare(b.organizationName) || this.fullName(a).localeCompare(this.fullName(b));
      return a.role.localeCompare(b.role) || this.fullName(a).localeCompare(this.fullName(b));
    });
  });

  ngOnInit(): void {
    this.service.getManagedUsers().subscribe({
      next: users => this.users.set(users),
      error: () => this.message.set('Unable to load users.')
    });
  }

  fullName(item: ManagedUserListItemDto): string {
    const name = `${item.firstName ?? ''} ${item.lastName ?? ''}`.trim();
    return name || item.email || `User ${item.userId}`;
  }

  trackBy(item: ManagedUserListItemDto): string {
    return `${item.userId}-${item.organizationId}-${item.role}`;
  }

  openDetail(userId: number): void {
    void this.router.navigate(['/users', userId]);
  }
}
